import { Badge, Icon, lookupStatusConfig } from "@shared/ui";
import { Fragment, useContext, useState } from "react";
import toast from "react-hot-toast";

import { ClientContext } from "../App";
import { executeAndPollPolicyAction, getPolicyReport } from "../lib/automatic";
import { MAX_POLL_DURATION_MS, POLL_INTERVAL_MS } from "../lib/constants";
import PolicyActionConfirmModal from "./PolicyActionConfirmModal";

const DetailsPage = ({ title, badge, actions, getPolicyReportOptions, children }) => {
  const { selectedClient: clientId } = useContext(ClientContext);
  return (
    <Fragment>
      <div className="flex flex-col px-12 gap-y-9 h-fit">
        {/* Page header */}
        <div className="flex flex-row items-center gap-x-4">
          {/* Page title */}
          <h1 className="text-2xl font-bold inline-flex gap-3">{title}</h1>

          {/* Badge - typically a status */}
          {badge && <Badge status={badge} />}

          {/* Actions - supports copy and download buttons */}
          <div className="flex flex-row ml-auto">
            {actions.map((action) => (
              <h4
                key={action.type + action.label}
                className="text-right text-gray-500 bg-gray-50 hover:text-black hover:bg-gray-100 px-2 py-1 transition rounded-sm cursor-pointer mr-3"
                onClick={async () => {
                  if (action.type === "copy") {
                    navigator.clipboard.writeText(action.value);
                    toast.success("Copied to clipboard");
                  } else if (action.type === "download") {
                    const toastId = toast.loading(
                      "Generating policy report...",
                      { duration: Infinity, isReportDownload: true }
                    );
                    try {
                      const reportOptions = getPolicyReportOptions?.() ?? {};
                      const reportData = await getPolicyReport(
                        action.value,
                        clientId,
                        reportOptions
                      );
                      if (reportData && reportData.report) {
                        // Signal 100% — progress bar jumps to complete
                        toast.loading("Generating policy report...", {
                          id: toastId,
                          duration: Infinity,
                          isReportDownload: true,
                          completed: true,
                        });
                        setTimeout(() => {
                          toast.dismiss(toastId);
                          const link = document.createElement("a");
                          link.href = reportData.report;
                          const reportTermSuffix =
                            reportOptions.term === "renewal" ? "-renewal" : "";
                          link.download = `policy-report-${action.value}${reportTermSuffix}.pdf`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          toast.success("Report downloaded successfully");
                        }, 300);
                      } else {
                        toast.dismiss(toastId);
                        toast.error("Failed to download report");
                      }
                    } catch (error) {
                      console.error("Error downloading report:", error);
                      toast.dismiss(toastId);
                      toast.error(
                        `Error: ${error.message || "Failed to download report"}`
                      );
                    }
                  }
                }}
              >
                {action.label}
              </h4>
            ))}
          </div>
        </div>
        {/* Page details — md+ uses nowrap so the sidebar slot keeps width while validation reloads */}
        <div className="flex w-full flex-col gap-6 md:flex-row md:flex-nowrap md:items-start">
          {children}
        </div>
      </div>
    </Fragment>
  );
};

const DetailsPageColumn = ({ primary, children }) => {
  return (
    <div
      className={`flex flex-col gap-y-6 w-full ${
        primary
          ? "md:min-w-0 md:flex-1 md:basis-0"
          : "md:shrink-0 md:basis-[32%] md:min-w-[280px] md:max-w-md"
      }`}
    >
      {children}
    </div>
  );
};

const DetailsPageCard = ({
  title,
  children,
  iconStatus,
  badgeStatus,
  actions = [],
  policyId,
  onPolicyRefreshSuccess,
  isActionInProgress = false,
  getPolicyOptions = {},
}) => {
  const statusConfig = lookupStatusConfig(iconStatus);
  const { selectedClient: clientId } = useContext(ClientContext);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);

  const handleActionClick = (action) => {
    if (isActionInProgress) return;
    setSelectedAction(action);
    setModalOpen(true);
  };

  const handleConfirm = async () => {
    await executeAndPollPolicyAction(
      policyId,
      selectedAction.type,
      clientId,
      POLL_INTERVAL_MS,
      MAX_POLL_DURATION_MS,
      onPolicyRefreshSuccess,
      getPolicyOptions
    );
  };

  return (
    <>
      <div
        className={`flex flex-col gap-y-6 border border-gray-300 rounded p-8 pb-10`}
      >
        <div className="flex flex-row justify-between items-center">
          <h2 className=" text-base flex gap-1 items-center">
            {title}
            {iconStatus && (
              <>
                <Icon
                  icon={statusConfig.icon}
                  width={statusConfig.width}
                  height={statusConfig.height}
                />
              </>
            )}
          </h2>
          <div className="flex gap-2 items-center">
            {badgeStatus && <Badge status={badgeStatus} />}
            {actions.length > 0 && (
              <>
                {actions.map((action, index) => (
                  <DetailsPageAction
                    key={index}
                    action={action}
                    handleActionClick={handleActionClick}
                    disabled={isActionInProgress}
                  />
                ))}
              </>
            )}
          </div>
        </div>
        {children}
      </div>

      <PolicyActionConfirmModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        action={selectedAction}
        onConfirm={handleConfirm}
      />
    </>
  );
};

/**
 * A labelled value inside a DetailsPageCard. `className` positions it for the card's
 * own layout: column spacing in a row of fields, nothing when it runs full width.
 */
const DetailsPageField = ({ label, value, className = "" }) => (
  <div className={`flex flex-col ${className}`}>
    <h3 className="uppercase text-[0.625rem] font-medium">{label}</h3>
    <p className="text-sm break-words">{value}</p>
  </div>
);

const DetailsPageAction = ({ action, index, handleActionClick, disabled }) => {
  return (
    <div
      key={index}
      className={`px-2 py-1 transition rounded-sm text-sm ${
        disabled
          ? "text-gray-300 bg-gray-50 cursor-not-allowed"
          : "text-gray-500 bg-gray-50 hover:text-black hover:bg-gray-100 cursor-pointer"
      }`}
      onClick={() => !disabled && handleActionClick(action)}
    >
      {action.label}
    </div>
  );
};

export { DetailsPage, DetailsPageCard, DetailsPageColumn, DetailsPageField };
